import React, { useEffect, useState, useCallback } from 'react';
import { Globe, Trash2, Plus, X, Check } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/Modal';
import { tabService, type WorkspaceTab } from '@/services/tabs';
import type { ApiBrowserTab } from '@/types';
import { useTabMonitorSessionOptional } from '@/context/TabMonitorSessionContext';
import { toast } from 'sonner';

interface TabsInWorkspaceCardProps {
  workspaceId: string;
  /** Can the current user assign/unassign tabs (editor+). */
  canEdit: boolean;
  refreshKey: number;
  /** Increment to open the add-tabs modal from a parent action. */
  openAddTabsSignal?: number;
  onChange: () => void;
}

function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url.slice(0, 32);
  }
}

export const TabsInWorkspaceCard: React.FC<TabsInWorkspaceCardProps> = ({
  workspaceId,
  canEdit,
  refreshKey,
  openAddTabsSignal = 0,
  onChange,
}) => {
  const sessionCtx = useTabMonitorSessionOptional();
  const sessionInfo = sessionCtx?.sessionInfo ?? null;
  const [tabs, setTabs] = useState<WorkspaceTab[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [unassignTarget, setUnassignTarget] = useState<WorkspaceTab | null>(null);

  const fetchTabs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await tabService.getWorkspaceTabsExpanded(workspaceId);
      setTabs(rows);
    } catch (err) {
      setError((err as Error).message ?? 'Failed to load tabs');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    void fetchTabs();
  }, [fetchTabs, refreshKey]);

  useEffect(() => {
    if (openAddTabsSignal > 0 && canEdit && sessionInfo?.id) {
      setShowAddModal(true);
    }
  }, [openAddTabsSignal, canEdit, sessionInfo?.id]);

  const confirmUnassign = async () => {
    if (!unassignTarget) return;
    try {
      await tabService.unassignWorkspaceTab(workspaceId, unassignTarget.id);
      toast.success('Tab removed from workspace');
      setUnassignTarget(null);
      void fetchTabs();
      onChange();
    } catch (err) {
      toast.error((err as Error).message ?? 'Failed to remove tab');
    }
  };

  return (
    <Card className="p-6">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h3 className="text-lg font-semibold text-khiimori-text-primary flex items-center gap-2 min-w-0">
          <Globe className="w-5 h-5 flex-shrink-0" />
          <span className="truncate">Tabs in this workspace</span>
          {tabs.length > 0 && (
            <span className="text-xs font-normal text-khiimori-text-muted flex-shrink-0">({tabs.length})</span>
          )}
        </h3>
        {canEdit && sessionInfo?.id && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setShowAddModal(true)}
            className="gap-1.5 shrink-0 whitespace-nowrap"
          >
            <Plus className="w-4 h-4" />
            Add tabs
          </Button>
        )}
      </div>

      {canEdit && !sessionInfo?.id && (
        <p className="text-xs text-khiimori-text-muted mb-3 -mt-2">
          Open <strong>Tabs → Live</strong> first so Khiimori can read your current browser tabs.
        </p>
      )}

      {loading ? (
        <p className="text-sm text-khiimori-text-muted">Loading…</p>
      ) : error ? (
        <p className="text-sm text-red-500">{error}</p>
      ) : tabs.length === 0 ? (
        <p className="text-sm text-khiimori-text-muted">
          No tabs shared yet. {canEdit ? 'Use "Add tabs" to share tabs from the current session.' : ''}
        </p>
      ) : (
        <ul className="space-y-2">
          {tabs.map((t) => (
            <li
              key={t.id}
              className="flex items-center gap-3 p-2 rounded-md bg-khiimori-bg-elevated/40 border border-khiimori-border/40 group"
            >
              {t.favicon ? (
                <img
                  src={t.favicon}
                  alt=""
                  className="w-4 h-4 flex-shrink-0"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : (
                <Globe className="w-4 h-4 text-khiimori-text-muted flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-khiimori-text-primary truncate">
                  {t.title || t.url || t.id}
                </p>
                {t.url && (
                  <p className="text-xs text-khiimori-text-muted truncate">{hostnameOf(t.url)}</p>
                )}
              </div>
              {t.url && (
                <a
                  href={t.url}
                  target="_blank"
                  rel="noreferrer"
                  className="p-1.5 rounded-md text-khiimori-text-muted hover:text-blue-500 hover:bg-blue-500/10 transition-colors opacity-0 group-hover:opacity-100"
                  title="Open in new tab"
                >
                  <Globe className="w-4 h-4" />
                </a>
              )}
              {canEdit && (
                <button
                  type="button"
                  title="Remove from workspace"
                  onClick={() => setUnassignTarget(t)}
                  className="p-1.5 rounded-md text-khiimori-text-muted hover:text-red-500 hover:bg-red-500/10 transition-colors opacity-0 group-hover:opacity-100"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {showAddModal && sessionInfo?.id && (
        <AddTabsModal
          workspaceId={workspaceId}
          sessionId={sessionInfo.id}
          alreadyAssigned={new Set(tabs.map((t) => t.id))}
          onClose={() => setShowAddModal(false)}
          onAdded={() => {
            setShowAddModal(false);
            void fetchTabs();
            onChange();
          }}
        />
      )}

      <ConfirmDialog
        isOpen={unassignTarget !== null}
        onClose={() => setUnassignTarget(null)}
        onConfirm={() => void confirmUnassign()}
        title="Remove tab"
        message={
          unassignTarget
            ? `Remove "${unassignTarget.title ?? unassignTarget.url ?? 'this tab'}" from the workspace? Other members will lose access.`
            : ''
        }
        confirmText="Remove"
        variant="danger"
      />
    </Card>
  );
};

interface AddTabsModalProps {
  workspaceId: string;
  sessionId: string;
  alreadyAssigned: Set<string>;
  onClose: () => void;
  onAdded: () => void;
}

const AddTabsModal: React.FC<AddTabsModalProps> = ({
  workspaceId,
  sessionId,
  alreadyAssigned,
  onClose,
  onAdded,
}) => {
  const [tabs, setTabs] = useState<ApiBrowserTab[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { tabs: rows } = await tabService.getTabsBySession(sessionId, 1, 200);
        if (cancelled) return;
        setTabs(rows);
      } catch (err) {
        if (!cancelled) setError((err as Error).message ?? 'Failed to load tabs');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const filtered = tabs.filter((t) => {
    if (alreadyAssigned.has(t.id)) return false;
    if (!search.trim()) return true;
    const needle = search.toLowerCase();
    return t.title.toLowerCase().includes(needle) || t.url.toLowerCase().includes(needle);
  });

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (selected.size === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      for (const id of selected) {
        await tabService.assignTabToWorkspace(workspaceId, id);
      }
      toast.success(`Added ${selected.size} tab${selected.size === 1 ? '' : 's'}`);
      onAdded();
    } catch (err) {
      const msg = (err as Error).message ?? 'Failed to add tabs';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Add tabs to workspace" size="lg">
      <div className="space-y-3">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter tabs by title or URL…"
          className="w-full px-3 py-2 text-sm rounded-md border border-khiimori-border bg-khiimori-bg-elevated/50 text-khiimori-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500/40"
        />

        {loading ? (
          <p className="text-sm text-khiimori-text-muted py-4 text-center">Loading tabs…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-khiimori-text-muted py-4 text-center">
            {tabs.length === 0
              ? 'No tabs in the current session yet.'
              : 'No matching tabs to add.'}
          </p>
        ) : (
          <ul className="max-h-80 overflow-y-auto space-y-1">
            {filtered.map((t) => (
              <li key={t.id}>
                <label className="flex items-center gap-2 p-2 rounded-md hover:bg-khiimori-bg-elevated/60 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selected.has(t.id)}
                    onChange={() => toggle(t.id)}
                    className="mt-0.5"
                  />
                  {t.favicon ? (
                    <img
                      src={t.favicon}
                      alt=""
                      className="w-4 h-4 flex-shrink-0"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <Globe className="w-4 h-4 text-khiimori-text-muted flex-shrink-0" />
                  )}
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm text-khiimori-text-primary truncate">
                      {t.title || t.url}
                    </span>
                    <span className="block text-xs text-khiimori-text-muted truncate">{t.url}</span>
                  </span>
                  {selected.has(t.id) && <Check className="w-4 h-4 text-primary-500" />}
                </label>
              </li>
            ))}
          </ul>
        )}

        {error && (
          <p className="text-sm text-red-500" role="alert">
            {error}
          </p>
        )}

        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-khiimori-text-muted">
            {selected.size} selected
          </p>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={selected.size === 0 || submitting}
            >
              {submitting ? 'Adding…' : `Add ${selected.size || ''}`.trim()}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default TabsInWorkspaceCard;
